<template>
  <section>
    <slot name="label">
      <h2 class="mb-0">{{ $t('Color') }}</h2>
      <h4 class="text-dark">{{ $t('Color') }} options below</h4>
    </slot>
    <b-row class="essentials-select-color-option-row mt-2 mx-0">
      <b-col v-for="option in options" :key="option.name" cols="auto" class="essentials-select-color-option-col py-1 px-0 pr-2">
        <b-button
          v-if="
            (!option.size && allSizeAttributesAreFalse(option) && option.status === 'I') ||
            option.smallInStock ||
            option.largeInStock ||
            option.XSInStock ||
            option.SInStock ||
            option.MInStock ||
            option.LInStock ||
            option.XLInStock
          "
          :ref="option.name"
          :class="{ selected: option.name == color }"
          class="color-option text-center p-0"
          block
          variant="outline-primary"
          :title="option.name"
          :aria-label="`Choose ${option.name}`"
          @focus="setFocus(option.name)"
          @focusout="unsetFocus"
          @click="setColor(option.name)"
        >
          <img v-if="option.image" v-lazy="getImage(option.image.original)" alt="" width="100%" :style="{ width: imageWidth }" />
        </b-button>
        <div v-else title="Out of Stock" class="text-center">
          <div class="no-stock">
            <img
              v-if="option.image"
              v-lazy="getImage(option.image.original)"
              alt=""
              width="100%"
              class="color-option text-center p-0"
              :style="{ width: imageWidth }"
            />
            <svg aria-hidden="true"><line x1="0" y1="100%" x2="100%" y2="0" /></svg>
          </div>
          <div class="text-danger copy" v-text="$t('product.outOfStock')" />
        </div>
        <h3 v-if="showName && options.length > 1" class="text-center text-primary mt-2 mb-0">
          <template
            v-if="
              (!option.size && allSizeAttributesAreFalse(option) && option.status === 'I') ||
              option.smallInStock ||
              option.largeInStock ||
              option.XSInStock ||
              option.SInStock ||
              option.MInStock ||
              option.LInStock ||
              option.XLInStock
            "
          >
            <span style="cursor: pointer" @click="setColor(option.name)">{{ option.name }}</span>
          </template>
          <template v-else>
            <span class="text-muted">{{ option.name }}</span>
          </template>
        </h3>
      </b-col>
    </b-row>
    <ValidationProvider v-slot="{ errors }" vid="p-color-select" :rules="{ required: { allowFalse: false } }" slim>
      <input v-model="value" type="text" class="d-none" hidden />
      <span v-if="errors && errors[0]" class="text-danger">Select a {{ $t('Color') }}</span>
    </ValidationProvider>
  </section>
</template>

<script>
import getImage from '@/mixins/getImage';
import { KeyboardNavigationMixins } from '@/mixins/keyboardNavigation';

export default {
  name: 'EssentialsColorSelect',
  mixins: [getImage, KeyboardNavigationMixins],
  props: {
    value: {
      type: String,
      required: false,
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    showName: {
      type: Boolean,
      default: true,
    },
    imageWidth: {
      type: String,
      default: '100%',
    },
    autoSelectOnCreate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      color: this.value,
    };
  },
  computed: {
    navigableOptions() {
      return this.options
        .filter(function (option) {
          return (
            option.smallInStock ||
            option.largeInStock ||
            option.XSInStock ||
            option.SInStock ||
            option.MInStock ||
            option.LInStock ||
            option.XLInStock
          );
        })
        .map((option) => option.name);
    },
  },
  created() {
    if (
      this.autoSelectOnCreate &&
      (!this.value || !this.color || !this.options?.some((color) => color?.name === this.value && !color?.allOutOfStock)) &&
      this.options.length >= 1
    ) {
      this.setColor(this.options?.find((color) => color && !color.allOutOfStock)?.name);
    }
  },
  updated() {
    if (
      (!this.value || !this.color || !this.options?.some((color) => color?.name === this.value && !color?.allOutOfStock)) &&
      this.options.length >= 1
    ) {
      this.setColor(this.options?.find((color) => color && !color.allOutOfStock)?.name);
    }
  },
  methods: {
    allSizeAttributesAreFalse(option) {
      const sizes = ['smallInStock', 'largeInStock', 'XSInStock', 'SInStock', 'MInStock', 'LInStock', 'XLInStock'];
      return sizes.every((size) => !option?.[size]);
    },
    setColor(color) {
      if (color == this.color) {
        return;
      } else {
        this.color = color;
      }
      this.$emit('input', this.color);
      this.$emit('change');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.essentials-select-color-option-row .essentials-select-color-option-col {
  flex: 0 0 25%;
  max-width: 25%;
  .color-option {
    border-radius: 10px;
    border: 2px solid $dp-gray-4;
    height: auto;
    overflow: hidden;
    &.selected {
      border: 2px solid $dp-blue;
      background-color: rgba(204, 236, 249, 0.5);
    }
    &:hover,
    &:active,
    &:focus {
      &:not(.disabled) {
        border: 2px solid $dp-blue;
        box-shadow: 0 0 0 0.2rem $dp-light-blue;
        background-color: rgba(204, 236, 249, 0.5) !important;
      }
    }
  }
}
.no-stock {
  border-radius: 10px;
  border: 2px solid white;
  height: auto;
  position: relative;
  img {
    width: 90%;
    filter: grayscale(80%);
  }
  svg {
    width: 80%;
    height: 80%;
    position: absolute;
    top: 10%;
    left: 10%;
    line {
      stroke: $dp-gray-3;
      stroke-width: 4;
    }
  }
}
</style>
